// Fecha de las notificaciones, con respaldo mientras el backend no la envia.
//
// Cuando "Evento/Listado" y el additionalData de la push incluyan el campo
// "fecha", este helper la toma sin que haya que tocar la interfaz. Mientras
// tanto se usa el momento en que la notificacion se vio por primera vez en
// el cliente, registrado aqui: asi la fila nunca aparece sin dato y el
// listado se puede separar por dia.

// idlog -> timestamp de la primera vez que se vio la notificacion.
// Se conserva aunque el listado se recargue tras un borrado, para que las
// horas no se reinicien en cada refresco.
const registroLlegadas = new Map();

const CAMPOS_FECHA = ["fecha", "fechaEvento", "fechaCreacion", "fechaRegistro"];

/**
 * Convierte a Date los formatos que puede mandar el backend.
 * Si algun endpoint empieza a enviar dd/MM/yyyy, este es el unico lugar a cambiar.
 */
const parsearFecha = (valor) => {
    if (!valor) return null;
    if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;

    if (typeof valor === "number") {
        const fecha = new Date(valor);
        return isNaN(fecha.getTime()) ? null : fecha;
    }

    const texto = String(valor).trim();
    if (!texto) return null;

    // Formato legado de ASP.NET: /Date(1757012435000-0700)/
    const legado = texto.match(/^\/Date\((-?\d+)/);
    if (legado) {
        const fecha = new Date(Number(legado[1]));
        return isNaN(fecha.getTime()) ? null : fecha;
    }

    // ISO: 2026-09-03T14:20:00 o 2026-09-03 14:20:00
    if (/^\d{4}-\d{2}-\d{2}/.test(texto)) {
        const fecha = new Date(texto.replace(" ", "T"));
        return isNaN(fecha.getTime()) ? null : fecha;
    }

    // MM/dd/yyyy [HH:mm:ss], mismo criterio que Helpers/FormatFecha.js
    const partes = texto.split(" ");
    const separada = partes[0].split("/");
    if (separada.length === 3) {
        const [mes, dia, anio] = separada;
        const hora = partes[1] || "00:00:00";
        const fecha = new Date(`${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}T${hora}`);
        return isNaN(fecha.getTime()) ? null : fecha;
    }

    const fecha = new Date(texto);
    return isNaN(fecha.getTime()) ? null : fecha;
};

/** Registra la llegada de una notificacion si aun no se conocia. */
export const registrarLlegadaNotificacion = (clave) => {
    if (clave === undefined || clave === null || clave === "") return;
    if (!registroLlegadas.has(clave)) registroLlegadas.set(clave, Date.now());
};

/** Registra de golpe todo un listado. */
export const registrarLlegadaListado = (listado, obtenerClave) => {
    (Array.isArray(listado) ? listado : []).forEach((item) => registrarLlegadaNotificacion(obtenerClave(item)));
};

/**
 * Fecha de la notificacion: la del backend si viene, si no la de llegada al
 * cliente. Nunca regresa null para que la fila siempre tenga algo que mostrar.
 */
// El backend puede serializar "fecha" o "Fecha" segun la configuracion de
// JSON, y en JS las propiedades distinguen mayusculas. Se busca sin importar.
const leerFechaDelBackend = (notificacion) => {
    if (!notificacion || typeof notificacion !== "object") return null;
    const claves = Object.keys(notificacion);

    for (const campo of CAMPOS_FECHA) {
        const clave = claves.find((k) => k.toLowerCase() === campo.toLowerCase());
        if (!clave) continue;
        const fecha = parsearFecha(notificacion[clave]);
        if (fecha) return fecha;
    }
    return null;
};

export const obtenerFechaNotificacion = (notificacion, clave) => {
    const delBackend = leerFechaDelBackend(notificacion);
    if (delBackend) return { fecha: delBackend, esReal: true };
    const llegada = registroLlegadas.get(clave);
    return { fecha: new Date(llegada || Date.now()), esReal: false };
};

const MINUTO = 60000;
const HORA = 3600000;
const DIA = 86400000;

const dosDigitos = (numero) => String(numero).padStart(2, "0");
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const soloDia = (fecha) => new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()).getTime();

/** "hace 8 min", "hace 3 h", "Ayer 18:40", "31 ago 09:15". */
export const formatearFechaRelativa = (fecha) => {
    if (!fecha) return "";
    const ahora = new Date();
    const diferencia = ahora.getTime() - fecha.getTime();
    const hora = `${dosDigitos(fecha.getHours())}:${dosDigitos(fecha.getMinutes())}`;

    if (diferencia < MINUTO) return "hace un momento";
    if (diferencia < HORA) return `hace ${Math.floor(diferencia / MINUTO)} min`;

    const diasDiferencia = Math.round((soloDia(ahora) - soloDia(fecha)) / DIA);
    if (diasDiferencia <= 0) {
        const horas = Math.floor(diferencia / HORA);
        return horas < 1 ? `hace ${Math.floor(diferencia / MINUTO)} min` : `hace ${horas} h`;
    }
    if (diasDiferencia === 1) return `Ayer ${hora}`;
    if (fecha.getFullYear() === ahora.getFullYear()) {
        return `${fecha.getDate()} ${MESES[fecha.getMonth()]} ${hora}`;
    }
    return `${fecha.getDate()} ${MESES[fecha.getMonth()]} ${fecha.getFullYear()}`;
};

/** Etiqueta del separador de dia: "Hoy", "Ayer", "31 de agosto". */
export const obtenerEtiquetaDia = (fecha) => {
    if (!fecha) return "";
    const ahora = new Date();
    const diasDiferencia = Math.round((soloDia(ahora) - soloDia(fecha)) / DIA);
    if (diasDiferencia <= 0) return "Hoy";
    if (diasDiferencia === 1) return "Ayer";
    if (fecha.getFullYear() === ahora.getFullYear()) {
        return `${fecha.getDate()} ${MESES[fecha.getMonth()]}`;
    }
    return `${fecha.getDate()} ${MESES[fecha.getMonth()]} ${fecha.getFullYear()}`;
};

/** Fecha completa para el tooltip de la fila. */
export const formatearFechaCompleta = (fecha, esReal) => {
    if (!fecha) return "";
    const texto = `${dosDigitos(fecha.getDate())}/${dosDigitos(fecha.getMonth() + 1)}/${fecha.getFullYear()} ${dosDigitos(fecha.getHours())}:${dosDigitos(fecha.getMinutes())}`;
    return esReal ? texto : `${texto} (hora de recepcion en este equipo)`;
};
